import Immutable from 'immutable';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

type ValidationProps = Readonly<{
  isFormValid: boolean;
}>;

type ValidationState = Readonly<{ isUsed: boolean, isChanged: boolean, value: string | number | boolean }>;

type ValidationComponent = Readonly<{
  state: ValidationState;
}>;

type ValidationComponents = Readonly<{
  [name: string] : ValidationComponent
}>;

type Rule = {
  rule: (value: string | number | boolean, components: ValidationComponents) => boolean,
  hint: (value: string | number | boolean) => string
};

type ValidationRules = {[rule: string]: Rule};

const ComponentsContext = createContext<ValidationComponents>({});
const SetComponentsContext = createContext<React.Dispatch<React.SetStateAction<ValidationComponents>>>(() => {});

const IsFormValidContext = createContext<boolean>(false);
const SetIsFormValidContext = createContext<React.Dispatch<React.SetStateAction<boolean>>>(() => {});

type ServerValidationErrorsMap = Immutable.Map<Readonly<string[]>, string>;
const ServerValidationErrorsContext = createContext<ServerValidationErrorsMap>(Immutable.Map());
const SetServerValidationErrorsContext = createContext<React.Dispatch<React.SetStateAction<ServerValidationErrorsMap>>>(() => {});

function ValidationProvider ({ children }: { children?: React.ReactNode; }) {
  const [isFormValid, setIsFormValid] = useState(true);
  const [serverValidationErrors, setServerValidationErrors ] = useState<Immutable.Map<Readonly<string[]>, string>>(Immutable.Map());

  const [components, setComponents] = useState<ValidationComponents>({});

  return (
    <ComponentsContext.Provider value={components}>
      <SetComponentsContext.Provider value={setComponents}>
        <IsFormValidContext.Provider value={isFormValid}>
          <SetIsFormValidContext.Provider value={setIsFormValid}>
            <ServerValidationErrorsContext.Provider value={serverValidationErrors}>
              <SetServerValidationErrorsContext.Provider value={setServerValidationErrors}>
                {children}
              </SetServerValidationErrorsContext.Provider>
            </ServerValidationErrorsContext.Provider>
          </SetIsFormValidContext.Provider>
        </IsFormValidContext.Provider>
      </SetComponentsContext.Provider>
    </ComponentsContext.Provider>
  );
}

function ValidationFormWithoutProvider ({ children, className, onSubmit }: {
  className?: string;
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  children?: React.ReactNode;
}) {
  return (
    <form className={className} onSubmit={onSubmit}>
      {children}
    </form>
  );
}


type Props = {
  className?: string;
  children?: React.ReactNode;
  onSubmit?: (event: React.FormEvent<HTMLFormElement>) => void;
  withProvider?: boolean;
};

function ValidationForm ({ children, className, onSubmit, withProvider }: Props) {
  if (withProvider === true) {
    return (
      <ValidationProvider>
        <ValidationFormWithoutProvider onSubmit={onSubmit} className={className}>
          {children}
        </ValidationFormWithoutProvider>
      </ValidationProvider>
    );
  } else {
    return (
      <ValidationFormWithoutProvider onSubmit={onSubmit} className={className}>
        {children}
      </ValidationFormWithoutProvider>
    );
  }
}

type ComponentType<P, S, SS> = { new (props: P): React.Component<P, S, SS> };

function ValidationWrapper2<P extends ValidationProps, S, SS> (props: Omit<P, keyof ValidationProps> & {component: ComponentType<P, S, SS>}) {
  const Component = props.component;
  const [ isFormValid ] = useIsFormValid();

  let componentProps: Omit<P, keyof ValidationProps> & {component?: ComponentType<P, S, SS>} = {...props};
  delete componentProps.component;

  const componentProps2: P = {...componentProps, isFormValid: isFormValid} as P;

  return (
    <Component {...componentProps2} />
  );
}

function ValidationWrapper<P extends ValidationProps, S = {}, SS = any> (component: ComponentType<P, S, SS>) {
  class Component extends React.Component<Omit<P, keyof ValidationProps>, S, SS> {
    constructor (props: Omit<P, keyof ValidationProps>) {
      super(props);
    }

    render () {
      return (
        <ValidationProvider>
          <ValidationWrapper2 component={component} {...this.props} />
        </ValidationProvider>
      );
    }
  }

  return Component;
}

function useValidate (validations: Readonly<string[]>, rules: Readonly<ValidationRules>, validationPath: Readonly<string[]> | string): [
  string | undefined,
  (value: string | number | boolean, components: ValidationComponents) => void
] {
  const [hint, setHint] = useState<string | undefined>(undefined);
  const setIsFormValid = useContext(SetIsFormValidContext);

  const serverValidationErrors = useContext(ServerValidationErrorsContext);
  
  const validate = useCallback((value: string | number | boolean, components: ValidationComponents) => {
    let newHint: string | undefined = undefined;

    let isValid = true;

    if (validationPath != null) {
      const serverValidationPath = typeof validationPath === 'string' ? [validationPath] : validationPath;
      const serverValidationError = serverValidationErrors.get(serverValidationPath);
      if (serverValidationError != null) {
        newHint = serverValidationError;
        isValid = false;
      }
    }

    if (isValid){
      for (const validation of validations) {
        if (!rules[validation].rule(value, components)) {
          newHint = rules[validation].hint(value);
          isValid = false;
          break;
        }
      }
    }

    setIsFormValid(isValid);
    setHint(newHint);
  }, [validations, rules, validationPath]);

  return [hint, validate];
}

function useIsFormValid (): [boolean, React.Dispatch<React.SetStateAction<boolean>>] {
  return [
    useContext(IsFormValidContext),
    useContext(SetIsFormValidContext)
  ];
}

type FormActionsType = Readonly<{
  add: (name: string, value: string | number | boolean) => void,
  update: (name: string, value: string | number | boolean) => void,
  remove: (name: string) => void
}>;

function useFormComponents (): [ValidationComponents, FormActionsType] {
  const setComponents = useContext(SetComponentsContext);
  
  const FormActions = useMemo(() => {
    return {
      add: (name: string, value: string | number | boolean) => {
        setComponents(components => {
          console.assert(components[name] == null, `duplicate component registered: ${name}`);
          const result = {
            ...components,
            [name] : { state: { isUsed: true, isChanged: false, value: value } }
          };
          return result;
        });
      },
      update: (name: string, value: string | number | boolean) => {
        setComponents(components => {
          console.assert(components[name] != null, `updating component that was not registered: ${name}`);
          const result = {
            ...components,
            [name] : { state: { isUsed: true, isChanged: true, value: value } }
          };

          return result;
        });
      },
      remove: (name: string) => {
        setComponents(components => {
          const component = components[name];
          if (component != null) {
            let result = {
              ...components
            };

            delete result[name];

            return result;
          }
          return components;
        });
      }
    } as const;
  }, []);
  
  
  return [
    useContext(ComponentsContext),
    FormActions
  ];
}

export type { ValidationProps, Rule, ValidationComponents, ValidationComponent, ValidationState };

export { ValidationProvider, ValidationWrapper, useIsFormValid, useValidate, SetServerValidationErrorsContext, useFormComponents };

export default ValidationForm;